import { useTranslation } from 'next-i18next'
import { type ComponentProps } from 'react'
import { useTheme } from 'styled-components'

import { TRANSLATION_KEYS } from '@/constants/localization'
import { useLocalUserPreferences } from '@/features/profile/hooks/useLocalUserPreferences'
import { BodyText } from '@/ui/atoms/body-text'
import { Checkbox } from '@/ui/atoms/checkbox'
import { Spacing } from '@/ui/atoms/spacing'

/**
 * A checkbox component that allows users to opt-in to pre-populating their name in future forms.
 * When enabled, the user's name will be automatically filled in forms across the application.
 * The preference is stored in local storage and persists between sessions.
 *
 * @returns A checkbox with explanatory text
 */
export const PrePopulateNameCheckbox = ({
  onChange,
  ...props
}: Omit<ComponentProps<typeof Checkbox>, 'id' | 'label'>) => {
  const { shouldPrePopulateName, setShouldPrePopulateName } = useLocalUserPreferences()
  const { colors } = useTheme()
  const { t } = useTranslation(TRANSLATION_KEYS.COMMON)

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setShouldPrePopulateName(e.target.checked)
    onChange?.(e)
  }

  return (
    <Checkbox
      checked={shouldPrePopulateName}
      onChange={handleChange}
      id="pre-populate-name"
      label={
        <Spacing mTop="-0.2rem">
          <BodyText size="medium" color={colors.grey.primary} weight="regular">
            {t('common:forms.inputs.pre_populate_name.label')}
          </BodyText>
        </Spacing>
      }
      {...props}
    />
  )
}
